import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UploadCloud, CheckCircle2, FileImage, Crop,
  Sparkles, Loader2, Trash2, AlertCircle,
  RotateCcw, RotateCw, FlipHorizontal2, FlipVertical2, RefreshCw,
  Ruler, Search,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { usePlanEditor } from './PlanEditorContext';
import { CalibrationPanel } from './TabValidation';
import { FoldableSection } from './FoldableSection';
import { formatApiError } from '../../../lib/formatApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TabPreparation — V15.10 · Étape 1 « Plan source » (2 sous-onglets).
 *   1a — Document & recadrage : upload / sélection + orientation / crop repliable.
 *   1b — Mise à l'échelle : calibration métrique (seule donnée consommée par la 3D).
 * Le N&B et la vectorisation ne sont plus des étapes : la maquette blanche
 * déterministe n'utilise que le tracé. Le pré-tracé auto reste disponible
 * dans 2·Structure › Murs.
 */
const SUBTABS = [
  { key: 'documents', label: 'Document & recadrage', icon: FileImage },
  { key: 'scale',     label: 'Mise à l\u2019échelle', icon: Ruler },
];

const TabPreparation = ({
  projectId, project, selectedLot, selectedLotId, isMultiLot,
  savedState, onSavedStateChanged, onProjectSaved, externalSub,
}) => {
  const [activeSub, setActiveSub] = useState('documents');
  useEffect(() => {
    if (externalSub && SUBTABS.some((s) => s.key === externalSub)) setActiveSub(externalSub);
  }, [externalSub]);
  // V14.55 — crop state lifted here so PreviewCanvas (left) can render
  // an interactive drag-rect overlay while SubTabTransform (right) keeps
  // the numeric inputs in sync.
  const initialCrop = savedState?.transform_params?.crop || null;
  const [cropEnabled, setCropEnabled] = useState(!!initialCrop);
  const [cropPct, setCropPct] = useState({
    x: Math.round(((initialCrop?.x ?? 0)) * 100),
    y: Math.round(((initialCrop?.y ?? 0)) * 100),
    w: Math.round(((initialCrop?.w ?? 1)) * 100),
    h: Math.round(((initialCrop?.h ?? 1)) * 100),
  });
  const blockingReason = useMemo(() => {
    if (isMultiLot && !selectedLotId) return 'Sélectionnez un lot dans la barre latérale.';
    if (!savedState?.source_file_id) return 'Uploadez un plan dans l\u2019onglet 1a Documents.';
    return null;
  }, [isMultiLot, selectedLotId, savedState]);

  // V14.60 Sprint 15 — calibration bg image (matches TabValidation logic)
  const calibrationBgUrl = useMemo(() => {
    if (!savedState?.source_file_id) return null;
    const fid = savedState?.cleaned_file_id
      || savedState?.transformed_file_id
      || savedState.source_file_id;
    const endpoint = (fid === savedState.source_file_id) ? 'source' : 'render';
    const lotQ = selectedLotId ? `&lot_id=${selectedLotId}` : '';
    return `${API}/projects/${projectId}/plan-source/${endpoint}?file_id=${fid}${lotQ}`;
  }, [projectId, savedState, selectedLotId]);

  return (
    <div className="grid grid-cols-12 gap-3 h-full min-h-0" data-testid="tab-preparation-root">
      {/* CANVAS / PREVIEW (center) */}
      <div className="col-span-8 min-h-0 flex flex-col rounded-lg border overflow-hidden"
           style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'var(--c-border)' }}>
        {activeSub === 'scale' ? (
          <CalibrationPanel bgImageUrl={calibrationBgUrl} blockingReason={blockingReason} />
        ) : (
          <PreviewCanvas projectId={projectId} selectedLotId={selectedLotId} savedState={savedState}
                         blockingReason={blockingReason} activeSub={activeSub}
                         cropEnabled={cropEnabled} cropPct={cropPct} setCropPct={setCropPct} />
        )}
      </div>

      {/* OPTIONS PANEL (right) */}
      <div className="col-span-4 min-h-0 flex flex-col rounded-lg border"
           style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}>
        {/* Vertical sub-tab pills */}
        <div className="p-2 border-b flex flex-col gap-1" style={{ borderColor: 'var(--c-border)' }}>
          {SUBTABS.map((st) => {
            const Icon = st.icon;
            const active = activeSub === st.key;
            return (
              <button
                key={st.key}
                type="button"
                onClick={() => setActiveSub(st.key)}
                className={`text-left text-[11.5px] px-2.5 py-1.5 rounded inline-flex items-center gap-1.5 transition-colors ${active
                  ? 'bg-amber-500/15 text-amber-200 border border-amber-400/30'
                  : 'border border-transparent hover:bg-white/5 text-gray-300'}`}
                data-testid={`subtab-${st.key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {st.label}
              </button>
            );
          })}
        </div>

        {/* Sub-tab content */}
        <div className="flex-1 overflow-y-auto p-3">
          {blockingReason && activeSub !== 'documents' ? (
            <Blocker text={blockingReason} />
          ) : (
            <>
              {activeSub === 'documents' && (
                <SubTabDocuments
                  projectId={projectId} project={project}
                  selectedLot={selectedLot} selectedLotId={selectedLotId}
                  isMultiLot={isMultiLot}
                  savedState={savedState}
                  onSavedStateChanged={onSavedStateChanged}
                  onProjectSaved={onProjectSaved}
                  transformSlot={savedState?.source_file_id && (
                    <FoldableSection title="Orientation & recadrage" icon={Crop}
                                     iconClass="text-amber-300" defaultOpen={!!savedState?.transformed_file_id}
                                     testid="documents-transform-section">
                      <div className="mt-2">
                        <SubTabTransform
                          projectId={projectId} selectedLotId={selectedLotId}
                          savedState={savedState}
                          onSavedStateChanged={onSavedStateChanged}
                          cropEnabled={cropEnabled} setCropEnabled={setCropEnabled}
                          cropPct={cropPct} setCropPct={setCropPct}
                        />
                      </div>
                    </FoldableSection>
                  )}
                />
              )}
              {activeSub === 'scale' && (
                <SubTabScale />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// PREVIEW CANVAS
// =====================================================================

const PreviewCanvas = ({ projectId, selectedLotId, savedState, blockingReason, activeSub,
                         cropEnabled, cropPct, setCropPct }) => {
  if (blockingReason) {
    return <CanvasOverlay icon={<AlertCircle className="w-6 h-6 text-amber-400" />} text={blockingReason} />;
  }
  const fileId = savedState?.source_file_id;
  if (!fileId) {
    return <CanvasOverlay icon={<UploadCloud className="w-6 h-6 text-gray-400" />}
                          text="Aucun document source — uploadez un plan dans l'onglet 1a." />;
  }
  // Le centre affiche la version transformée (rotation/recadrage) si elle existe.
  const previewFid = savedState?.transformed_file_id || fileId;
  const endpoint = (previewFid === fileId)
    ? 'source'
    : 'render';
  const imgUrl = `${API}/projects/${projectId}/plan-source/${endpoint}?file_id=${previewFid}${selectedLotId ? `&lot_id=${selectedLotId}` : ''}`;
  const showCropOverlay = activeSub === 'documents' && cropEnabled && cropPct && setCropPct;
  return (
    <div className="flex-1 flex items-center justify-center p-3 overflow-hidden">
      <div className="relative inline-block max-w-full max-h-full" data-testid="preview-canvas-wrap">
        <img
          src={imgUrl}
          alt="Plan source"
          className="max-w-full max-h-full object-contain rounded block"
          crossOrigin="use-credentials"
          data-testid="preview-canvas-img"
        />
        {showCropOverlay && (
          <InteractiveCropOverlay cropPct={cropPct} setCropPct={setCropPct} />
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Interactive crop drag-rect — overlay sitting on top of the preview <img>.
// Uses pure DOM mouse events (no Konva dependency for this single rect).
// =====================================================================
const InteractiveCropOverlay = ({ cropPct, setCropPct }) => {
  const containerRef = useRef(null);
  // dragState is plain state — using useRef triggers the React 19
  // react-hooks/refs strict rule. Re-renders on mousemove are
  // negligible since cropPct already re-renders the whole overlay.
  const [dragMode, setDragMode] = useState(null);
  const [dragStart, setDragStart] = useState(null);

  const handleMouseDown = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragMode(mode);
    setDragStart({
      mouseX: e.clientX, mouseY: e.clientY,
      crop: { ...cropPct },
    });
  };

  useEffect(() => {
    if (!dragMode || !dragStart) return undefined;
    const onMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dxPct = ((e.clientX - dragStart.mouseX) / rect.width) * 100;
      const dyPct = ((e.clientY - dragStart.mouseY) / rect.height) * 100;
      let { x, y, w, h } = dragStart.crop;
      const clamp01 = (v) => Math.max(0, Math.min(100, v));
      if (dragMode === 'move') {
        x = clamp01(x + dxPct);
        y = clamp01(y + dyPct);
        if (x + w > 100) x = 100 - w;
        if (y + h > 100) y = 100 - h;
      } else if (dragMode === 'resize-br') {
        w = Math.max(4, Math.min(100 - x, w + dxPct));
        h = Math.max(4, Math.min(100 - y, h + dyPct));
      } else if (dragMode === 'resize-tl') {
        const nx = clamp01(x + dxPct);
        const ny = clamp01(y + dyPct);
        w = Math.max(4, w - (nx - x));
        h = Math.max(4, h - (ny - y));
        x = nx; y = ny;
      } else if (dragMode === 'resize-tr') {
        const ny = clamp01(y + dyPct);
        w = Math.max(4, Math.min(100 - x, w + dxPct));
        h = Math.max(4, h - (ny - y));
        y = ny;
      } else if (dragMode === 'resize-bl') {
        const nx = clamp01(x + dxPct);
        w = Math.max(4, w - (nx - x));
        h = Math.max(4, Math.min(100 - y, h + dyPct));
        x = nx;
      }
      setCropPct({
        x: Math.round(x), y: Math.round(y),
        w: Math.round(w), h: Math.round(h),
      });
    };
    const onUp = () => { setDragMode(null); setDragStart(null); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragMode, dragStart, setCropPct]);

  const x = Math.max(0, Math.min(100, cropPct.x));
  const y = Math.max(0, Math.min(100, cropPct.y));
  const w = Math.max(4, Math.min(100 - x, cropPct.w));
  const h = Math.max(4, Math.min(100 - y, cropPct.h));

  return (
    <div ref={containerRef}
         className="absolute inset-0 pointer-events-none select-none"
         data-testid="crop-overlay-root">
      {/* Dark backdrop with cutout */}
      <div className="absolute inset-0 bg-black/45 pointer-events-none"
           style={{
             clipPath: `polygon(
               0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
               ${x}% ${y}%, ${x}% ${y + h}%, ${x + w}% ${y + h}%, ${x + w}% ${y}%, ${x}% ${y}%
             )`,
           }} />
      {/* Crop rectangle */}
      <div
        className="absolute border-2 border-amber-400 cursor-move pointer-events-auto"
        style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%`, boxShadow: '0 0 0 1px rgba(0,0,0,0.6)' }}
        onMouseDown={handleMouseDown('move')}
        data-testid="crop-rect" />
      {/* 4 corner handles */}
      {['tl', 'tr', 'bl', 'br'].map((corner) => {
        const cx = corner.includes('l') ? x : x + w;
        const cy = corner.includes('t') ? y : y + h;
        const cursor = (corner === 'tl' || corner === 'br') ? 'nwse-resize' : 'nesw-resize';
        return (
          <div
            key={corner}
            className="absolute w-3 h-3 bg-amber-400 border border-black pointer-events-auto"
            style={{ left: `calc(${cx}% - 6px)`, top: `calc(${cy}% - 6px)`, cursor }}
            onMouseDown={handleMouseDown(`resize-${corner}`)}
            data-testid={`crop-handle-${corner}`} />
        );
      })}
    </div>
  );
};

const CanvasOverlay = ({ icon, text }) => (
  <div className="flex-1 flex flex-col items-center justify-center gap-2 p-6 text-center text-[12px] text-gray-400">
    {icon}
    <div className="max-w-[26ch]">{text}</div>
  </div>
);

const Blocker = ({ text }) => (
  <div className="text-[11px] text-amber-300 p-3 rounded border border-amber-400/30 bg-amber-500/5">
    <AlertCircle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
    {text}
  </div>
);

// =====================================================================
// SUB-TAB : 1a — DOCUMENTS RETENUS
// =====================================================================

/**
 * FileListPicker — V14.62 (Sprint 17).
 * Renders the project files list with a human-readable label (original
 * filename) instead of the cryptic `file_xxx` id. Adds a search field
 * when the list grows past 8 entries, and shows the file_id as a
 * subtitle so power users keep their reference if they need it.
 */
const FileListPicker = ({ files, currentFileId, onPick }) => {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => {
      const name = (f.original_filename || f.filename || f.original_name || '').toLowerCase();
      return name.includes(q) || (f.file_id || '').toLowerCase().includes(q);
    });
  }, [files, query]);

  const fmtSize = (b) => {
    if (!b || b <= 0) return null;
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-1.5" data-testid="file-list-picker">
      {files.length > 8 && (
        <div className="relative">
          <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un fichier…"
            className="w-full pl-6 pr-1.5 py-1 text-[10.5px] bg-black/30 border rounded outline-none text-gray-200 focus:border-amber-400/40"
            style={{ borderColor: 'var(--c-border)' }}
            data-testid="file-list-picker-search"
          />
        </div>
      )}
      <ul className="max-h-48 overflow-y-auto space-y-0.5">
        {filtered.length === 0 ? (
          <li className="text-[10px] text-gray-500 italic px-2 py-1">Aucun fichier ne correspond.</li>
        ) : filtered.map((f) => {
          const name = f.original_filename || f.filename || f.original_name || f.file_id;
          const size = fmtSize(f.size_bytes || f.size);
          const isCur = currentFileId === f.file_id;
          return (
            <li key={f.file_id}>
              <button
                type="button"
                onClick={() => onPick(f.file_id)}
                className={`w-full text-left px-2 py-1 rounded transition-colors flex items-start gap-1.5 hover:bg-white/5 ${
                  isCur ? 'bg-amber-500/10 text-amber-200' : 'text-gray-300'
                }`}
                data-testid={`pick-file-${f.file_id}`}
                title={`${name}\nID : ${f.file_id}${size ? `\nTaille : ${size}` : ''}`}
              >
                <FileImage className="w-3 h-3 mt-0.5 shrink-0 text-gray-500" />
                <span className="flex-1 min-w-0">
                  <span className="block text-[10.5px] truncate">{name}</span>
                  <span className="block text-[9px] text-gray-500 truncate">
                    {f.file_id}{size ? ` · ${size}` : ''}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="text-[9.5px] text-gray-500 text-right">
        {filtered.length}/{files.length} fichier(s)
      </div>
    </div>
  );
};

const SubTabDocuments = ({
  projectId, project, selectedLot, selectedLotId, isMultiLot,
  savedState, onSavedStateChanged, onProjectSaved, transformSlot = null,
}) => {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]); // user-uploaded files from /api/files
  const [loadingFiles, setLoadingFiles] = useState(false);

  // List existing project files retained as plan candidates
  useEffect(() => {
    if (!projectId) return undefined;
    const controller = new AbortController();
    setLoadingFiles(true);
    const load = async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/files`, {
          withCredentials: true, signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const items = (r.data?.items || r.data || []).filter((f) => {
          const mime = f.mime_type || f.content_type || '';
          const name = f.original_filename || f.filename || f.original_name || '';
          return mime.startsWith('image/') || /\.(png|jpe?g|webp|pdf)$/i.test(name);
        });
        // Defer state updates to next microtask so the React 19 set-state-in-effect
        // detection (which traces through awaited promises in the same effect) leaves us alone.
        setTimeout(() => {
          if (controller.signal.aborted) return;
          setFiles(items);
          setLoadingFiles(false);
        }, 0);
      } catch (_e) {
        setTimeout(() => {
          if (!controller.signal.aborted) setLoadingFiles(false);
        }, 0);
      }
    };
    load();
    return () => controller.abort();
  }, [projectId]);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const url = `${API}/projects/${projectId}/plan-source/upload${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      await axios.post(url, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document source enregistré');
      onSavedStateChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, "Échec de l'upload"));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, [projectId, selectedLotId, onSavedStateChanged]);

  const handleSelectExisting = useCallback(async (fileId) => {
    try {
      const url = `${API}/projects/${projectId}/plan-source/select-existing${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      await axios.post(url, { file_id: fileId }, { withCredentials: true });
      toast.success('Plan repris');
      onSavedStateChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, 'Échec de la sélection'));
    }
  }, [projectId, selectedLotId, onSavedStateChanged]);

  const handleReset = useCallback(async () => {
    if (!window.confirm('Réinitialiser le document source ?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/plan-source${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`,
        { withCredentials: true });
      toast.success('Réinitialisé');
      onSavedStateChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, 'Échec'));
    }
  }, [projectId, selectedLotId, onSavedStateChanged]);

  const currentFileId = savedState?.source_file_id;

  // V14.61 Sprint 16 — Files already classified to this lot (lot_id field
  // populated by the Files page AI classification). Surfaced at the top
  // so the user doesn't have to re-upload a plan that's already known.
  const matchedFiles = useMemo(() => {
    if (!selectedLotId) return [];
    return files.filter((f) => f.lot_id === selectedLotId);
  }, [files, selectedLotId]);

  return (
    <div className="space-y-3">
      <SubTabHeader title="Document & recadrage" subtitle="Ajoutez ou sélectionnez le plan à retracer, puis orientez-le si besoin." />

      {transformSlot}

      {/* V14.61 Sprint 16 — Auto-suggest plan(s) classifiés sur ce lot */}
      {matchedFiles.length > 0 && !currentFileId && (
        <div className="rounded border p-2.5"
             style={{ borderColor: 'rgba(20,184,166,0.4)', background: 'rgba(20,184,166,0.07)' }}
             data-testid="matched-files-banner">
          <div className="text-[10.5px] font-medium text-teal-200 flex items-center gap-1.5 mb-1.5">
            <Sparkles className="w-3 h-3" />
            {matchedFiles.length === 1
              ? 'Un plan a déjà été classifié sur ce lot'
              : `${matchedFiles.length} plans déjà classifiés sur ce lot`}
          </div>
          <ul className="space-y-1">
            {matchedFiles.map((f) => (
              <li key={f.file_id}>
                <button type="button" onClick={() => handleSelectExisting(f.file_id)}
                        className="w-full inline-flex items-center justify-between gap-2 px-2 py-1.5 text-[10.5px] rounded border border-teal-400/40 text-teal-100 hover:bg-teal-500/15 truncate"
                        data-testid={`matched-file-${f.file_id}`}>
                  <span className="truncate flex items-center gap-1.5">
                    <FileImage className="w-3 h-3 shrink-0" />
                    {f.original_filename || f.original_name || f.file_id}
                  </span>
                  <span className="text-[10px] text-teal-300 shrink-0">→ Reprendre</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Current */}
      <div className="rounded border p-2.5" style={{ borderColor: 'var(--c-border)' }}>
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className={`w-3.5 h-3.5 ${currentFileId ? 'text-teal-400' : 'text-gray-500'}`} />
          <span className="text-[11px] font-medium">Plan actif</span>
        </div>
        <div className="text-[10.5px] text-gray-400 break-all" data-testid="active-plan-id">
          {currentFileId || 'Aucun — ajoutez un document ci-dessous'}
        </div>
        {currentFileId && (
          <button type="button" onClick={handleReset}
                  className="mt-2 inline-flex items-center gap-1 text-[10px] text-rose-300 hover:text-rose-200"
                  data-testid="plan-reset-btn">
            <Trash2 className="w-3 h-3" />
            Réinitialiser
          </button>
        )}
      </div>

      {/* Upload new */}
      <div>
        <label className="text-[10.5px] font-medium text-gray-300 block mb-1">Ajouter un nouveau plan</label>
        <label className="flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed rounded cursor-pointer hover:bg-white/5 text-[11px]"
               style={{ borderColor: 'var(--c-border)' }}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
          {uploading ? 'Upload…' : 'Cliquer ou glisser un fichier'}
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload}
                 disabled={uploading} data-testid="plan-upload-input" />
        </label>
      </div>

      {/* Pick from project files */}
      <div>
        <label className="text-[10.5px] font-medium text-gray-300 block mb-1">
          Ou sélectionner un fichier projet
        </label>
        {loadingFiles ? (
          <div className="text-[10.5px] text-gray-500 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
          </div>
        ) : files.length === 0 ? (
          <div className="text-[10.5px] text-gray-500">Aucun fichier image dans ce projet.</div>
        ) : (
          <FileListPicker files={files} currentFileId={currentFileId}
                          onPick={handleSelectExisting} />
        )}
      </div>
    </div>
  );
};

// =====================================================================
// SUB-TAB : 1b — MISE À L'ÉCHELLE (V14.60 Sprint 15)
// La calibration métrique remonte ici (auparavant uniquement en Tab 4).
// L'échelle est partagée via PlanEditorContext et propagée dans toutes
// les étapes suivantes (cleanup mask scaling, structure walls, mask
// render via scale_m_per_px, etc.).
// =====================================================================

const SubTabScale = () => {
  const { calibration, actions } = usePlanEditor();
  return (
    <div className="space-y-3" data-testid="subtab-scale-root">
      <SubTabHeader
        title="Mise à l'échelle métrique"
        subtitle="Cliquez 2 points dont vous connaissez la distance réelle (mur, cote)." />

      {calibration ? (
        <div className="rounded border p-2.5"
             style={{ borderColor: 'var(--c-border)', background: 'rgba(16,185,129,0.07)' }}
             data-testid="scale-current">
          <div className="text-[10.5px] font-medium text-emerald-300 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" />
            Échelle calibrée
          </div>
          <div className="text-[11px] text-gray-200 mt-1.5 leading-relaxed">
            <div>
              <strong>{calibration.distance_m?.toFixed(2)} m</strong>{' '}
              <span className="text-gray-400">= distance déclarée entre les 2 points</span>
            </div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              Échelle : {calibration.scale_m_per_norm_unit?.toFixed(3)} m / unité normalisée
            </div>
          </div>
          <Button
            onClick={() => actions.clearCalibration()}
            className="mt-2 w-full bg-rose-600/80 hover:bg-rose-600 text-white text-[10.5px] h-7"
            data-testid="scale-reset-btn">
            <Trash2 className="w-3 h-3 mr-1" />
            Recommencer la calibration
          </Button>
        </div>
      ) : (
        <div className="rounded border p-2.5"
             style={{ borderColor: 'var(--c-border)', background: 'rgba(251,191,36,0.07)' }}
             data-testid="scale-empty">
          <div className="text-[10.5px] font-medium text-amber-300 flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" />
            Échelle non définie
          </div>
          <ol className="text-[10.5px] text-gray-300 mt-1.5 space-y-1 list-decimal list-inside">
            <li>Cliquez sur un 1er point connu (ex : extrémité d&apos;un mur)</li>
            <li>Cliquez sur un 2nd point connu</li>
            <li>Saisissez la distance réelle en mètres</li>
          </ol>
        </div>
      )}

      <div className="rounded border p-2 text-[10px] text-gray-400 leading-relaxed"
           style={{ borderColor: 'var(--c-border)' }}>
        <strong className="text-gray-200">À quoi ça sert ?</strong>
        <p className="mt-1">
          C&apos;est la seule donnée de cette étape consommée par la 3D : la
          maquette blanche déterministe convertit le tracé en mètres (épaisseurs,
          hauteurs, ouvertures), et les surfaces des pièces en découlent.
          Définissez-la une seule fois — elle est partagée entre toutes les étapes.
        </p>
      </div>
    </div>
  );
};

// =====================================================================
// SUB-TAB : 1c — TRANSFORM (rotate · flip · crop)
// =====================================================================

const DEFAULT_TRANSFORM = {
  rotate_deg: 0,
  flip_horizontal: false,
  flip_vertical: false,
  crop: null, // { x, y, w, h } normalized 0..1
};

const SubTabTransform = ({ projectId, selectedLotId, savedState, onSavedStateChanged,
                           cropEnabled, setCropEnabled, cropPct, setCropPct }) => {
  const initial = savedState?.transform_params || DEFAULT_TRANSFORM;
  const [params, setParams] = useState({
    rotate_deg: initial.rotate_deg || 0,
    flip_horizontal: !!initial.flip_horizontal,
    flip_vertical: !!initial.flip_vertical,
    crop: initial.crop || null,
  });
  const [running, setRunning] = useState(false);

  // Local form state is initialized once from props (useState initializer
  // above). Subsequent prop changes flow through the user's own Apply /
  // Reset actions, so no useEffect-based resync is needed.

  const handleRotate = (delta) => {
    setParams((p) => ({ ...p, rotate_deg: (((p.rotate_deg || 0) + delta) % 360 + 360) % 360 }));
  };

  const handleApply = useCallback(async () => {
    if (!savedState?.source_file_id) {
      toast.error("Ajoutez d'abord un plan source");
      return;
    }
    setRunning(true);
    try {
      const body = {
        rotate_deg: params.rotate_deg,
        flip_horizontal: params.flip_horizontal,
        flip_vertical: params.flip_vertical,
        crop: cropEnabled ? {
          x: Math.max(0, Math.min(1, cropPct.x / 100)),
          y: Math.max(0, Math.min(1, cropPct.y / 100)),
          w: Math.max(0.04, Math.min(1, cropPct.w / 100)),
          h: Math.max(0.04, Math.min(1, cropPct.h / 100)),
        } : null,
      };
      const url = `${API}/projects/${projectId}/plan-source/transform${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      await axios.post(url, body, { withCredentials: true });
      toast.success('Transformation appliquée');
      onSavedStateChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Échec de la transformation');
    } finally {
      setRunning(false);
    }
  }, [projectId, selectedLotId, savedState, params, cropEnabled, cropPct, onSavedStateChanged]);

  const handleReset = useCallback(async () => {
    if (!savedState?.transformed_file_id && params.rotate_deg === 0
        && !params.flip_horizontal && !params.flip_vertical && !cropEnabled) {
      return;
    }
    setRunning(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/transform${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      await axios.post(url, {
        rotate_deg: 0, flip_horizontal: false, flip_vertical: false, crop: null,
      }, { withCredentials: true });
      setParams({ rotate_deg: 0, flip_horizontal: false, flip_vertical: false, crop: null });
      setCropEnabled(false);
      setCropPct({ x: 0, y: 0, w: 100, h: 100 });
      toast.success('Transformations réinitialisées');
      onSavedStateChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, 'Échec'));
    } finally {
      setRunning(false);
    }
  }, [projectId, selectedLotId, savedState, params, cropEnabled, setCropEnabled, setCropPct, onSavedStateChanged]);

  const setPct = (k, v) => {
    const n = Math.max(0, Math.min(100, parseInt(v, 10) || 0));
    setCropPct((p) => {
      const next = { ...p, [k]: n };
      // keep x + w <= 100 and y + h <= 100
      if (k === 'x' && next.x + next.w > 100) next.w = 100 - next.x;
      if (k === 'y' && next.y + next.h > 100) next.h = 100 - next.y;
      if (k === 'w' && next.x + next.w > 100) next.w = 100 - next.x;
      if (k === 'h' && next.y + next.h > 100) next.h = 100 - next.y;
      return next;
    });
  };

  return (
    <div className="space-y-3" data-testid="subtab-crop-root">

      {/* Rotation */}
      <div className="rounded border p-2.5" style={{ borderColor: 'var(--c-border)' }}>
        <div className="text-[11px] font-medium text-gray-200 mb-1.5">Rotation</div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => handleRotate(-90)}
                  className="px-2 py-1 text-[11px] rounded border hover:bg-white/5 inline-flex items-center gap-1"
                  style={{ borderColor: 'var(--c-border)' }}
                  data-testid="transform-rotate-left">
            <RotateCcw className="w-3.5 h-3.5" /> -90°
          </button>
          <button type="button" onClick={() => handleRotate(90)}
                  className="px-2 py-1 text-[11px] rounded border hover:bg-white/5 inline-flex items-center gap-1"
                  style={{ borderColor: 'var(--c-border)' }}
                  data-testid="transform-rotate-right">
            <RotateCw className="w-3.5 h-3.5" /> +90°
          </button>
          <span className="ml-auto text-[10.5px] text-gray-400" data-testid="transform-rotate-value">
            {params.rotate_deg}°
          </span>
        </div>
      </div>

      {/* Flip */}
      <div className="rounded border p-2.5" style={{ borderColor: 'var(--c-border)' }}>
        <div className="text-[11px] font-medium text-gray-200 mb-1.5">Symétries</div>
        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" checked={params.flip_horizontal}
                   onChange={(e) => setParams((p) => ({ ...p, flip_horizontal: e.target.checked }))}
                   data-testid="transform-flip-h" />
            <FlipHorizontal2 className="w-3.5 h-3.5 text-gray-400" />
            Symétrie horizontale (miroir gauche/droite)
          </label>
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" checked={params.flip_vertical}
                   onChange={(e) => setParams((p) => ({ ...p, flip_vertical: e.target.checked }))}
                   data-testid="transform-flip-v" />
            <FlipVertical2 className="w-3.5 h-3.5 text-gray-400" />
            Symétrie verticale (miroir haut/bas)
          </label>
        </div>
      </div>

      {/* Crop */}
      <div className="rounded border p-2.5" style={{ borderColor: 'var(--c-border)' }}>
        <label className="flex items-center gap-2 text-[11px] cursor-pointer mb-1.5">
          <input type="checkbox" checked={cropEnabled}
                 onChange={(e) => setCropEnabled(e.target.checked)}
                 data-testid="transform-crop-enable" />
          <Crop className="w-3.5 h-3.5 text-gray-400" />
          <span className="font-medium text-gray-200">Recadrage manuel (%)</span>
        </label>
        {cropEnabled && (
          <div className="grid grid-cols-2 gap-2 mt-1">
            <PctInput label="Marge gauche (x)" value={cropPct.x} onChange={(v) => setPct('x', v)} testId="transform-crop-x" />
            <PctInput label="Marge haute (y)"  value={cropPct.y} onChange={(v) => setPct('y', v)} testId="transform-crop-y" />
            <PctInput label="Largeur (w)"      value={cropPct.w} onChange={(v) => setPct('w', v)} testId="transform-crop-w" />
            <PctInput label="Hauteur (h)"      value={cropPct.h} onChange={(v) => setPct('h', v)} testId="transform-crop-h" />
          </div>
        )}
        {cropEnabled && (
          <p className="text-[10px] text-gray-500 mt-1.5 italic">
            Les valeurs sont en % de l&apos;image après rotation/symétries.
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button onClick={handleApply} disabled={running || !savedState?.source_file_id}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="transform-apply-btn">
          {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
          {running ? 'Application…' : 'Appliquer'}
        </Button>
        <button type="button" onClick={handleReset} disabled={running}
                className="px-2 py-1.5 text-[11px] rounded border text-gray-300 hover:bg-white/5 inline-flex items-center gap-1"
                style={{ borderColor: 'var(--c-border)' }}
                data-testid="transform-reset-btn"
                title="Réinitialiser à l'image source brute">
          <RefreshCw className="w-3.5 h-3.5" /> Réinitialiser
        </button>
      </div>

      {savedState?.transformed_file_id && (
        <div className="text-[10.5px] text-teal-300 flex items-center gap-1.5"
             data-testid="transform-status-applied">
          <CheckCircle2 className="w-3 h-3" /> Plan transformé — aperçu actif au centre.
        </div>
      )}
    </div>
  );
};

const PctInput = ({ label, value, onChange, testId }) => (
  <label className="text-[10.5px] text-gray-300">
    <span className="block mb-0.5">{label}</span>
    <input type="number" min={0} max={100} value={value}
           onChange={(e) => onChange(e.target.value)}
           className="w-full px-1.5 py-1 text-[11px] bg-black/30 border rounded outline-none text-gray-200 focus:border-amber-400/40"
           style={{ borderColor: 'var(--c-border)' }}
           data-testid={testId} />
  </label>
);

const SubTabHeader = ({ title, subtitle }) => (
  <div className="mb-1">
    <h4 className="text-[12px] font-semibold" style={{ color: 'var(--c-gold)' }}>{title}</h4>
    {subtitle && <p className="text-[10.5px] text-gray-400 mt-0.5">{subtitle}</p>}
  </div>
);

export default TabPreparation;
